import React__default from 'react';
import { useFormContext, useFieldArray, useController } from 'react-hook-form';
import { Button } from '../../../atoms/Button.js';
import { Note } from '../../../atoms/Note.js';
import { Text } from '../../../atoms/Text.js';
import { WarningIcon } from '../../../icons/WarningIcon.js';
import { DropdownFormField } from './DropdownFormField.js';
import { ReadOnlyField } from './ReadOnlyFormField.js';
import { TextAreaFormField } from './TextAreaFormField.js';
import { TextFormField } from './TextFormField.js';
import './ObjectArrayFormField.scss';

/**
 * ObjectArrayFormField - A form field component for managing arrays of structured objects.
 *
 * Each entry in the array is an object with up to 2 configurable fields. Users can add/remove entries,
 * and each field can be text, textarea, or dropdown.
 *
 * @example
 * ```tsx
 * const schema = z.object({
 *   disclosures: z.array(z.object({
 *     name: z.string(),
 *     description: z.string(),
 *   })),
 * });
 *
 * <Form schema={schema} onSubmit={handleSubmit}>
 *   <ObjectArrayFormField
 *     schemaField="disclosures"
 *     labelText="Custom Disclosures"
 *     fields={[
 *       { key: 'name', label: 'Name', type: 'text', required: true },
 *       { key: 'description', label: 'Description', type: 'textarea', required: true },
 *     ]}
 *     entryLimit={10}
 *     addButtonText="Add Disclosure"
 *   />
 * </Form>
 * ```
 */
function ObjectArrayFormField({ schemaField, labelText, descriptionText, isReadOnly, isDisabled, required, className, fields, entryLimit, emptyText = 'No entries', }) {
    const { control } = useFormContext();
    const { fields: arrayFields, append, remove, } = useFieldArray({
        control,
        name: schemaField,
    });
    // Get array-level error using useController
    const { fieldState } = useController({
        control,
        name: schemaField,
    });
    const errorMessage = fieldState.error?.message;
    // Create default empty object for new entries
    const getDefaultEntry = () => {
        const entry = {};
        limitedFields.forEach((field) => {
            entry[field.key] = field.type === 'dropdown' ? null : '';
        });
        return entry;
    };
    const handleAdd = () => {
        if (!entryLimit || arrayFields.length < entryLimit) {
            append(getDefaultEntry());
        }
    };
    const handleRemove = (index) => {
        remove(index);
    };
    const isAtLimit = entryLimit !== undefined && arrayFields.length >= entryLimit;
    // Validate field count (max 2 fields)
    if (fields.length > 2) {
        console.warn(`ObjectArrayFormField: Maximum of 2 fields is supported. Received ${fields.length} fields. Only the first 2 will be used.`);
    }
    // Limit to first 2 fields
    const limitedFields = fields.slice(0, 2);
    // Read-only mode
    if (isReadOnly) {
        if (arrayFields.length === 0) {
            return (React__default.createElement(ReadOnlyField, { label: labelText, value: emptyText, description: descriptionText, required: required, className: className }));
        }
        const displayValue = arrayFields
            .map((field, index) => {
            const entry = field;
            const fieldValues = limitedFields
                .map((config) => {
                const value = entry[config.key];
                if (config.type === 'dropdown' && config.options) {
                    const option = config.options.find((opt) => opt.id === value);
                    return option ? option.label : value;
                }
                return value;
            })
                .filter(Boolean)
                .join(' • ');
            return `${index + 1}. ${fieldValues}`;
        })
            .join('\n');
        return (React__default.createElement(ReadOnlyField, { label: labelText, value: displayValue, description: descriptionText, required: required, className: className }));
    }
    return (React__default.createElement("div", { "data-rev-component": 'object-array-form-field', "data-rev-invalid": !!errorMessage, "data-rev-required-field": !!required, className: className },
        (labelText || descriptionText) && (React__default.createElement("div", null,
            labelText && (React__default.createElement(Text, { size: 'bodySmall', weight: 'semibold' },
                labelText,
                !required && (React__default.createElement(Text, { size: 'bodySmall', weight: 'regular', color: 'tertiary', tag: 'span' },
                    ' ',
                    "(optional)")))),
            descriptionText && (React__default.createElement("div", { className: 'object-array-description--rev' },
                React__default.createElement(Text, { size: 'caption', color: 'secondary' }, descriptionText))))),
        React__default.createElement("div", { className: 'object-array-entries--rev' }, arrayFields.length === 0 ? (React__default.createElement(Note, null,
            React__default.createElement(Text, { size: 'bodySmall', color: 'secondary' }, emptyText))) : (arrayFields.map((field, index) => {
            const entryFieldName = `${schemaField}.${index}`;
            return (React__default.createElement("div", { key: field.id, className: 'object-array-entry--rev' },
                React__default.createElement("div", { className: 'object-array-fields--rev' }, limitedFields.map((fieldConfig) => {
                    const fieldPath = `${entryFieldName}.${fieldConfig.key}`;
                    switch (fieldConfig.type) {
                        case 'text':
                            return (React__default.createElement("div", { key: fieldConfig.key, className: 'object-array-field--rev' },
                                React__default.createElement(TextFormField, { schemaField: fieldPath, labelText: fieldConfig.label, placeholder: fieldConfig.placeholder, required: fieldConfig.required, isDisabled: isDisabled, maxLength: fieldConfig.maxLength })));
                        case 'textarea':
                            return (React__default.createElement("div", { key: fieldConfig.key, className: 'object-array-field--rev' },
                                React__default.createElement(TextAreaFormField, { schemaField: fieldPath, labelText: fieldConfig.label, placeholder: fieldConfig.placeholder, required: fieldConfig.required, isDisabled: isDisabled, maxLength: fieldConfig.maxLength, showCharCount: fieldConfig.maxLength != null })));
                        case 'dropdown':
                            return (React__default.createElement("div", { key: fieldConfig.key, className: 'object-array-field--rev' },
                                React__default.createElement(DropdownFormField, { schemaField: fieldPath, labelText: fieldConfig.label, options: fieldConfig.options || [], required: fieldConfig.required, isDisabled: isDisabled })));
                        default:
                            return null;
                    }
                })),
                React__default.createElement("div", { className: 'object-array-remove-btn--rev' },
                    React__default.createElement(Button, { size: 'icon', variant: 'secondary', onPress: () => handleRemove(index), isDisabled: isDisabled, ariaLabel: `Remove entry ${index + 1}` },
                        React__default.createElement(CloseIcon, null)))));
        }))),
        React__default.createElement("div", { className: 'object-array-add-btn--rev' },
            React__default.createElement(Button, { variant: 'secondary', size: 'small', onPress: handleAdd, isDisabled: isDisabled || isAtLimit, ariaLabel: 'Add' }, "Add")),
        errorMessage && (React__default.createElement("div", { className: 'object-array-error--rev' },
            React__default.createElement("div", { className: 'object-array-icon--rev' },
                React__default.createElement(WarningIcon, null)),
            React__default.createElement(Text, { size: 'caption', color: 'error' }, errorMessage)))));
}
/** Close icon for remove button */
const CloseIcon = () => {
    return (React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '20', height: '21', viewBox: '0 0 20 21', fill: 'none' },
        React__default.createElement("path", { d: 'M7 13.5L13 7.5Z', fill: 'currentColor' }),
        React__default.createElement("path", { d: 'M7 7.5L13 13.5Z', fill: 'currentColor' }),
        React__default.createElement("path", { d: 'M7 13.5L13 7.5M7 7.5L13 13.5', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round' })));
};

export { ObjectArrayFormField };
//# sourceMappingURL=ObjectArrayFormField.js.map
